import { useCallback, useEffect, useRef, useState } from "react";
import type { ConnectionStatSummary } from "../types";
import type { WebRTCConnection } from "../models";

interface UseStatsWatcherProps {
  enabled: boolean;
  videoConnection: WebRTCConnection | null;
  audioConnection: WebRTCConnection | null;
  serverLatency: number;
  statsInterval?: number;
  onStatsUpdate?: (stats: ConnectionStatSummary) => void;
  isVideoConnected?: boolean;
  isAudioConnected?: boolean;
}

export const useWebRTCStatsWatcher = ({
  enabled,
  videoConnection,
  audioConnection,
  serverLatency,
  statsInterval = 1000,
  onStatsUpdate,
  isVideoConnected = false,
  isAudioConnected = false,
}: UseStatsWatcherProps) => {
  const [isCollectingStats, setCollectingStats] = useState(false);
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoBytesReceivedStartRef = useRef(0);
  const audioBytesReceivedStartRef = useRef(0);
  const previousVideoJitterBufferDelayRef = useRef(0.0);
  const previousVideoJitterBufferEmittedCountRef = useRef(0);
  const previousAudioJitterBufferDelayRef = useRef(0.0);
  const previousAudioJitterBufferEmittedCountRef = useRef(0);
  const previousPacketsLostRef = useRef(0);
  const previousPacketsReceivedRef = useRef(0);
  const previousVideoFramesDecodedRef = useRef(0);
  const previousVideoTotalDecodeTimeRef = useRef(0);
  const statsStartRef = useRef(new Date().getTime() / 1000);

  const stopStatsCollection = useCallback(() => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }

    // Reset all tracking variables
    videoBytesReceivedStartRef.current = 0;
    audioBytesReceivedStartRef.current = 0;
    previousVideoJitterBufferDelayRef.current = 0.0;
    previousVideoJitterBufferEmittedCountRef.current = 0;
    previousAudioJitterBufferDelayRef.current = 0.0;
    previousAudioJitterBufferEmittedCountRef.current = 0;
    previousPacketsLostRef.current = 0;
    previousPacketsReceivedRef.current = 0;
    previousVideoFramesDecodedRef.current = 0;
    previousVideoTotalDecodeTimeRef.current = 0;
    statsStartRef.current = new Date().getTime() / 1000;

    setCollectingStats(false);

    // Notify that stats collection has stopped
    onStatsUpdate?.({
      connectionLatency: 0.0,
      connectionVideoLatency: 0.0,
      connectionAudioLatency: 0.0,
      connectionPacketsReceived: 0,
      videoPacketsReceived: 0,
      connectionPacketsLost: 0,
      connectionPacketLossRate: 0,
      connectionStatType: "NA",
      connectionBytesReceived: "0.00 MBytes",
      connectionBytesSent: "0.00 MBytes",
      connectionAvailableBandwidth: 0,
      connectionCodec: "NA",
      connectionVideoDecoder: "NA",
      connectionResolution: "0x0",
      connectionFrameRate: 0,
      connectionVideoBitrate: "0.00",
      connectionAudioCodecName: "NA",
      connectionAudioBitrate: "0.00",
      connectionJitter: 0,
      connectionRtt: 0,
      connectionFreezeCount: 0,
      connectionTotalFreezesDuration: 0,
      connectionNackCount: 0,
      connectionPliCount: 0,
      connectionAvgDecodeTimeMs: 0,
      connectionFramesDecoded: 0,
    });
  }, []);

  const startStatsCollection = useCallback(() => {
    if (
      !enabled ||
      !videoConnection ||
      !audioConnection ||
      statsIntervalRef.current
    ) {
      return;
    }

    // Reset stats tracking variables
    videoBytesReceivedStartRef.current = 0;
    audioBytesReceivedStartRef.current = 0;
    previousVideoJitterBufferDelayRef.current = 0.0;
    previousVideoJitterBufferEmittedCountRef.current = 0;
    previousAudioJitterBufferDelayRef.current = 0.0;
    previousAudioJitterBufferEmittedCountRef.current = 0;
    previousVideoFramesDecodedRef.current = 0;
    previousVideoTotalDecodeTimeRef.current = 0;
    statsStartRef.current = new Date().getTime() / 1000;

    const statsLoop = setInterval(async () => {
      if (!audioConnection || !videoConnection) {
        stopStatsCollection();
        return;
      }

      try {
        const [videoStats, audioStats] = await Promise.all([
          videoConnection.getConnectionStats(),
          audioConnection.getConnectionStats(),
        ]);

        setCollectingStats(true);

        const now = new Date().getTime() / 1000;

        const framesDecodedDelta =
          videoStats.video.framesDecoded -
          previousVideoFramesDecodedRef.current;
        const decodeTimeDelta =
          videoStats.video.totalDecodeTime -
          previousVideoTotalDecodeTimeRef.current;
        const avgDecodeTimeMs =
          framesDecodedDelta > 0
            ? (decodeTimeDelta / framesDecodedDelta) * 1000
            : 0;

        const connectionStat: ConnectionStatSummary = {
          connectionLatency: 0.0,
          connectionVideoLatency:
            videoStats.general.currentRoundTripTime !== null
              ? videoStats.general.currentRoundTripTime * 1000.0
              : serverLatency,
          connectionAudioLatency:
            audioStats.general.currentRoundTripTime !== null
              ? audioStats.general.currentRoundTripTime * 1000.0
              : serverLatency,
          connectionPacketsReceived: 0,
          videoPacketsReceived: 0,
          connectionPacketsLost: 0,
          connectionPacketLossRate: 0,
          connectionStatType:
            videoStats.general.connectionType ===
            audioStats.general.connectionType
              ? videoStats.general.connectionType
              : `${videoStats.general.connectionType} / ${audioStats.general.connectionType}`,
          connectionBytesReceived:
            (
              (videoStats.general.bytesReceived +
                audioStats.general.bytesReceived) *
              1e-6
            ).toFixed(2) + " MBytes",
          connectionBytesSent:
            (
              (videoStats.general.bytesSent + audioStats.general.bytesSent) *
              1e-6
            ).toFixed(2) + " MBytes",
          connectionAvailableBandwidth:
            Math.round(
              ((videoStats.general.availableReceiveBandwidth +
                audioStats.general.availableReceiveBandwidth) /
                1e6) *
                100,
            ) / 100,
          connectionCodec: videoStats.video.codecName,
          connectionVideoDecoder: videoStats.video.decoder,
          connectionResolution: `${videoStats.video.frameWidth}x${videoStats.video.frameHeight}`,
          connectionFrameRate:
            videoStats.video.framesPerSecond ||
            Math.round(framesDecodedDelta / (now - statsStartRef.current)),
          connectionVideoBitrate: (
            (((videoStats.video.bytesReceived -
              videoBytesReceivedStartRef.current) /
              (now - statsStartRef.current)) *
              8) /
            1e6
          ).toFixed(2),
          connectionAudioCodecName: audioStats.audio.codecName,
          connectionAudioBitrate: (
            (((audioStats.audio.bytesReceived -
              audioBytesReceivedStartRef.current) /
              (now - statsStartRef.current)) *
              8) /
            1e3
          ).toFixed(2),
          connectionJitter: Math.max(
            videoStats.video.jitter,
            audioStats.audio.jitter,
          ),
          connectionRtt: videoStats.general.currentRoundTripTime * 1000.0,
          connectionFreezeCount: videoStats.video.freezeCount,
          connectionTotalFreezesDuration: videoStats.video.totalFreezesDuration,
          connectionNackCount: videoStats.video.nackCount,
          connectionAvgDecodeTimeMs: avgDecodeTimeMs,
          connectionPliCount: videoStats.video.pliCount,
          connectionFramesDecoded: videoStats.video.framesDecoded,
        };

        // Sum of video+audio packets
        const currentPacketsLost =
          videoStats.video.packetsLost + audioStats.audio.packetsLost;
        const currentPacketsReceived =
          videoStats.video.packetsReceived + audioStats.audio.packetsReceived;

        connectionStat.connectionPacketsReceived = currentPacketsReceived;
        connectionStat.videoPacketsReceived = videoStats.video.packetsReceived;
        connectionStat.connectionPacketsLost = currentPacketsLost;

        const packetsLostDelta =
          connectionStat.connectionPacketsLost - previousPacketsLostRef.current;
        const packetsReceivedDelta =
          connectionStat.connectionPacketsReceived -
          previousPacketsReceivedRef.current;

        const totalPackets = packetsReceivedDelta + packetsLostDelta;

        connectionStat.connectionPacketLossRate =
          totalPackets > 0
            ? Math.round((packetsLostDelta / totalPackets) * 100 * 100) / 100
            : 0;

        previousPacketsLostRef.current = currentPacketsLost;
        previousPacketsReceivedRef.current = currentPacketsReceived;

        connectionStat.connectionLatency = Math.max(
          connectionStat.connectionVideoLatency,
          connectionStat.connectionAudioLatency,
        );

        // Calculate jitter buffer latency
        connectionStat.connectionVideoLatency = Math.round(
          connectionStat.connectionVideoLatency +
            ((1000.0 *
              (videoStats.video.jitterBufferDelay -
                previousVideoJitterBufferDelayRef.current)) /
              (videoStats.video.jitterBufferEmittedCount -
                previousVideoJitterBufferEmittedCountRef.current) || 0),
        );
        previousVideoJitterBufferDelayRef.current =
          videoStats.video.jitterBufferDelay;
        previousVideoJitterBufferEmittedCountRef.current =
          videoStats.video.jitterBufferEmittedCount;

        connectionStat.connectionAudioLatency = Math.round(
          connectionStat.connectionAudioLatency +
            ((1000.0 *
              (audioStats.audio.jitterBufferDelay -
                previousAudioJitterBufferDelayRef.current)) /
              (audioStats.audio.jitterBufferEmittedCount -
                previousAudioJitterBufferEmittedCountRef.current) || 0),
        );
        previousAudioJitterBufferDelayRef.current =
          audioStats.audio.jitterBufferDelay;
        previousAudioJitterBufferEmittedCountRef.current =
          audioStats.audio.jitterBufferEmittedCount;

        // Format latency
        connectionStat.connectionLatency = Math.round(
          connectionStat.connectionLatency,
        );

        statsStartRef.current = now;
        videoBytesReceivedStartRef.current = videoStats.video.bytesReceived;
        audioBytesReceivedStartRef.current = audioStats.audio.bytesReceived;
        previousVideoFramesDecodedRef.current = videoStats.video.framesDecoded;
        previousVideoTotalDecodeTimeRef.current =
          videoStats.video.totalDecodeTime;

        // Call the stats callback

        onStatsUpdate?.(connectionStat);

        // Send stats via data channel
        if (videoConnection?.isDataChannelOpen()) {
          videoConnection.sendDataChannelMessage(
            "_stats_video," + JSON.stringify(videoStats.allReports),
          );
          videoConnection.sendDataChannelMessage(
            "_stats_audio," + JSON.stringify(audioStats.allReports),
          );
        }
      } catch (error) {
        console.error("Error collecting stats:", error);
        clearInterval(statsLoop);
        setCollectingStats(false);
      }
    }, statsInterval);

    statsIntervalRef.current = statsLoop;
  }, [
    enabled,
    statsInterval,
    videoConnection,
    audioConnection,
    stopStatsCollection,
  ]);

  useEffect(() => {
    if (
      enabled &&
      videoConnection &&
      audioConnection &&
      (isVideoConnected || isAudioConnected)
    ) {
      startStatsCollection();
    } else {
      stopStatsCollection();
    }

    return stopStatsCollection;
  }, [
    enabled,
    videoConnection,
    audioConnection,
    isVideoConnected,
    isAudioConnected,
    startStatsCollection,
    stopStatsCollection,
  ]);

  return {
    isCollectingStats,
    startStatsCollection,
    stopStatsCollection,
  };
};
